import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { CustomItemData } from '../components/AddCustomItemForm';

interface UseCustomLineItemProps {
  estimateId: string;
}

export const useCustomLineItem = ({ estimateId }: UseCustomLineItemProps) => {
  const queryClient = useQueryClient();

  const addCustomLineItem = useMutation({
    mutationFn: async (data: CustomItemData) => {
      // Generate UUID for self-referencing
      const customItemId = crypto.randomUUID();
      
      // Calculate final price
      const unitFinalPrice = data.baseCost * (1 + data.markupPercentage / 100);
      const totalFinalPrice = unitFinalPrice * data.quantity;

      // Get the next sort order
      const { data: existingItems } = await supabase
        .from('estimate_line_items')
        .select('sort_order')
        .eq('estimate_id', estimateId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextSortOrder = existingItems && existingItems.length > 0 
        ? (existingItems[0].sort_order || 0) + 1 
        : 0;

      // Insert the custom line item using raw SQL via RPC to allow setting the ID
      const { data: insertedItem, error } = await supabase
        .from('estimate_line_items')
        .insert({
          estimate_id: estimateId,
          component_type: 'custom',
          component_id: customItemId, // Self-referencing (same as the auto-generated id)
          description: data.description,
          base_unit: data.unit as any,
          quantity_in_base_units: data.quantity,
          unit_cost_snapshot: unitFinalPrice, // Customer-facing price after markup
          total_cost_snapshot: totalFinalPrice,
          manual_override: true,
          manual_unit_cost: data.baseCost, // Store actual base cost here
          manual_total_cost: totalFinalPrice,
          category_id: data.categoryId,
          sort_order: nextSortOrder
        })
        .select()
        .single();

      if (error) throw error;
      
      // Update the component_id to match the newly created id (self-referencing)
      const { error: updateError } = await supabase
        .from('estimate_line_items')
        .update({ component_id: insertedItem.id })
        .eq('id', insertedItem.id);
      
      if (updateError) throw updateError;
      
      return { ...insertedItem, component_id: insertedItem.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      toast.success('Custom item added successfully');
    },
    onError: (error) => {
      console.error('Error adding custom item:', error);
      toast.error('Failed to add custom item');
    }
  });

  const updateCustomLineItem = useMutation({
    mutationFn: async ({ 
      lineItemId, 
      data 
    }: { 
      lineItemId: string; 
      data: Partial<CustomItemData> 
    }) => {
      const updates: Record<string, any> = {};
      
      if (data.description !== undefined) {
        updates.description = data.description;
      }
      if (data.quantity !== undefined) {
        updates.quantity_in_base_units = data.quantity;
      }
      if (data.unit !== undefined) {
        updates.base_unit = data.unit;
      }
      if (data.categoryId !== undefined) {
        updates.category_id = data.categoryId;
      }
      if (data.baseCost !== undefined) {
        updates.manual_unit_cost = data.baseCost;
      }
      
      // Recalculate prices if base cost or markup changed
      if (data.baseCost !== undefined || data.markupPercentage !== undefined || data.quantity !== undefined) {
        // Get current item to fill in missing values
        const { data: currentItem } = await supabase
          .from('estimate_line_items')
          .select('manual_unit_cost, quantity_in_base_units, unit_cost_snapshot')
          .eq('id', lineItemId)
          .single();

        const baseCost = data.baseCost ?? currentItem?.manual_unit_cost ?? 0;
        const quantity = data.quantity ?? currentItem?.quantity_in_base_units ?? 1;
        const markupPercentage = data.markupPercentage ?? 25; // Default markup if not provided

        const unitFinalPrice = baseCost * (1 + markupPercentage / 100);
        const totalFinalPrice = unitFinalPrice * quantity;

        updates.unit_cost_snapshot = unitFinalPrice;
        updates.total_cost_snapshot = totalFinalPrice;
        updates.manual_total_cost = totalFinalPrice;
        updates.manual_unit_cost = baseCost;
      }

      const { data: updatedItem, error } = await supabase
        .from('estimate_line_items')
        .update(updates)
        .eq('id', lineItemId)
        .select()
        .single();

      if (error) throw error;
      return updatedItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      toast.success('Custom item updated');
    },
    onError: (error) => {
      console.error('Error updating custom item:', error);
      toast.error('Failed to update custom item');
    }
  });

  const deleteLineItem = useMutation({
    mutationFn: async (lineItemId: string) => {
      const { error } = await supabase
        .from('estimate_line_items')
        .delete()
        .eq('id', lineItemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', estimateId] });
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      toast.success('Line item removed');
    },
    onError: (error) => {
      console.error('Error deleting line item:', error);
      toast.error('Failed to remove line item');
    }
  });

  return {
    addCustomLineItem,
    updateCustomLineItem,
    deleteLineItem
  };
};
